import { useMemo } from "react";
import styled from "styled-components";
import { KeyValueObj } from "@/common/types";
import { KeyValueContainer } from "@/common/components/key-value-wrapper";

/**
 * MED-671 (AC4) — the completed onboarding submission, shown inside the company
 * information preview so the admin reviews the full record before deciding.
 *
 * The rest of the Company Information tab is a generic dump of whatever keys the
 * serializer returned, in serializer order, labelled by de-underscoring the key.
 * That is fine for browsing and useless for reviewing: what the company actually
 * submitted during onboarding is scattered through it, next to `next step` and
 * `settings`, and the fields that carry objects (the registered address) or
 * booleans (registration completed) are dropped by KeyValueContainer without
 * trace. This block states the submission explicitly, in the order it was
 * collected, and is deliberately additive — nothing was removed from the dump
 * below it, so no admin loses a field they were relying on.
 */

const SubmissionWrapper = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-bottom: 2rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid ${({ theme }) => theme.border.light};

  .section-title {
    font-size: 1rem;
    font-weight: 600;
    color: ${({ theme }) => theme.text.prim_100};
  }

  .section-note {
    font-size: 0.85rem;
    font-weight: 400;
    color: ${({ theme }) => theme.text.prim_200};
  }
`;

/** The uploads collected during onboarding, in the order they are asked for. */
const DOCUMENTS: [string, string][] = [
  ["certificate_of_incorporation", "Certificate of Incorporation"],
  ["nafdac_registration_certificate", "NAFDAC Registration Certificate"],
  ["tax_clearance", "Tax Clearance"],
  ["pcn_licence_certificate", "PCN Licence Certificate"],
  ["company_logo", "Company Logo"],
];

function humanize(value?: string) {
  if (!value) return "";
  const words = value.split("_").join(" ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** ISO timestamps and dates alike — the admin needs the day, not the second. */
function formatDate(value?: string) {
  if (!value) return "";
  return value.length >= 10 ? value.slice(0, 10) : value;
}

function formatAddress(address?: KeyValueObj) {
  if (!address) return "";

  return [
    address.building_number,
    address.street,
    address.address_line_one,
    address.city,
    address.lga,
    address.state,
  ]
    .filter(Boolean)
    .join(", ");
}

export interface IOnboardingSubmissionProps {
  company: KeyValueObj;
}

export function OnboardingSubmission({ company }: IOnboardingSubmissionProps) {
  const submission = useMemo(() => {
    const uploaded = DOCUMENTS.filter(([key]) => company[key]).map(
      ([, label]) => label
    );

    const rows: KeyValueObj[] = [
      // The stage-tracking value the queue and the approval guard both read, so
      // the admin sees the same fact the system acted on.
      {
        title: "Onboarding Status",
        value: company.registration_completed ? "Completed" : "Incomplete",
      },
      { title: "Last Completed Step", value: humanize(company.onboarding_step) },
      { title: "Company Name", value: company.name },
      { title: "Company Type", value: company.type },
      { title: "Classification", value: humanize(company.classification) },
      { title: "Email", value: company.email },
      { title: "Phone Number", value: company.phone_number },
      { title: "TIN", value: company.tin },
      { title: "RC Number", value: company.rc_number },
      {
        title: "Date of Establishment",
        value: formatDate(company.date_of_establishment),
      },
      { title: "Corporate Address", value: company.corporate_address },
      {
        title: "Registered Address",
        value: formatAddress(company.default_address),
      },
      { title: "PCN Licence Number", value: company.current_pcn_licence_number },
      {
        title: "PCN Licence Issued",
        value: formatDate(company.current_pcn_licence_iss_date),
      },
      {
        title: "PCN Licence Expires",
        value: formatDate(company.current_pcn_licence_exp_date),
      },
      {
        title: "PCN Verification",
        value: humanize(company.pcn_verification_status),
      },
      {
        title: "Documents Submitted",
        // Named rather than counted: "3 documents" tells a reviewer nothing
        // about which one is missing.
        value: uploaded.length ? uploaded.join(", ") : "None",
      },
    ];

    // An empty row reads as a missing answer when it is really a question this
    // company was never asked — PCN licence fields for a company with no
    // licence, for instance.
    return rows.filter((row) => row.value !== "" && row.value != null);
  }, [company]);

  // Before the fetch resolves there is nothing to review, and a block claiming
  // "Onboarding Status: Incomplete" from absent data would be worse than blank.
  if (!company?.id) return null;

  return (
    <SubmissionWrapper>
      <h3 className="section-title">
        Onboarding Submission{" "}
        <span className="section-note">— as submitted by the company</span>
      </h3>
      <KeyValueContainer data={submission} />
    </SubmissionWrapper>
  );
}
